/**
 * Asset type configuration and category helpers.
 * Used for icons, colors, and hierarchy validation.
 */

import {
  Grid3x3,
  Cable,
  Zap,
  Box,
  Network,
  Battery
} from 'lucide-react';

export enum AssetCategory {
  GENERATION = 'generation',
  STRING = 'string',
  CONVERSION = 'conversion',
  OTHER = 'other',
  /** Solar architecture: DC collection (modules, DC cable) */
  DC_SIDE = 'dc_side',
  /** Solar architecture: AC cable, LT panel */
  AC_SIDE = 'ac_side',
  /** Solar architecture: power transformer */
  TRANSMISSION = 'transmission',
  /** Solar architecture: battery, UPS — separate from main PV flow */
  OTHER_SYSTEM = 'other_system',
  /** PV module accessories — grouped with modules */
  ACCESSORY = 'accessory'
}

export type AssetTypeConfigItem = {
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  color: string;
  category: AssetCategory;
};

export const ASSET_TYPE_CONFIG: Record<string, AssetTypeConfigItem> = {
  'Solar Module': { icon: Grid3x3, color: '#0369A1', category: AssetCategory.GENERATION },
  'PV Module': { icon: Grid3x3, color: '#0369A1', category: AssetCategory.GENERATION },
  'Module': { icon: Grid3x3, color: '#0369A1', category: AssetCategory.GENERATION },
  'PV Array Module': { icon: Grid3x3, color: '#0369A1', category: AssetCategory.GENERATION },
  'SPV MODULE': { icon: Grid3x3, color: '#0369A1', category: AssetCategory.GENERATION },
  'String': { icon: Cable, color: '#8B5CF6', category: AssetCategory.STRING },
  'DC String': { icon: Cable, color: '#8B5CF6', category: AssetCategory.STRING },
  'Inverter': { icon: Zap, color: '#EF4444', category: AssetCategory.CONVERSION },
  'String Inverter': { icon: Zap, color: '#EF4444', category: AssetCategory.CONVERSION },
  'INVERTER': { icon: Zap, color: '#EF4444', category: AssetCategory.CONVERSION },
  'STRING INVERTER': { icon: Zap, color: '#EF4444', category: AssetCategory.CONVERSION },
  'Transformer': { icon: Box, color: '#F59E0B', category: AssetCategory.TRANSMISSION },
  'Power Transformer': { icon: Box, color: '#F59E0B', category: AssetCategory.TRANSMISSION },
  'Combiner Box': { icon: Box, color: '#10B981', category: AssetCategory.OTHER },
  /** DC string/module tree host — must not match generic "solar" → module (breaks layout + lazy modules). */
  'Solar DC Layout': { icon: Cable, color: '#0284C7', category: AssetCategory.OTHER },
  'DC Cable': { icon: Cable, color: '#6B7280', category: AssetCategory.DC_SIDE },
  'AC Cable': { icon: Cable, color: '#EC4899', category: AssetCategory.AC_SIDE },
  'LT Panel': { icon: Box, color: '#EC4899', category: AssetCategory.AC_SIDE },
  'Junction Box': { icon: Box, color: '#14B8A6', category: AssetCategory.OTHER },
  'Grid Connection': { icon: Network, color: '#8B5CF6', category: AssetCategory.OTHER },
  'PV Module Accessories': { icon: Grid3x3, color: '#0EA5E9', category: AssetCategory.ACCESSORY },
  /** O&M equipment — not a PV module; name contains "module" but must not use generation rules. */
  'Module Cleaning System': { icon: Box, color: '#0D9488', category: AssetCategory.OTHER },
  'BATTERY CELLS': { icon: Battery, color: '#6B7280', category: AssetCategory.OTHER_SYSTEM },
  'UPS System & Batteries': { icon: Battery, color: '#6B7280', category: AssetCategory.OTHER_SYSTEM },
  'Other': { icon: Box, color: '#6B7280', category: AssetCategory.OTHER }
};

/** Master / project catalog row — used to resolve icon & color by `masterAssetId`. */
export type AvailableAssetRef = {
  masterAssetId: string;
  name: string;
  description?: string;
};

export type GetAssetTypeConfigOptions = {
  masterAssetId?: string;
  /** Project-asset catalog name — improves matching when `assetType` is generic. */
  catalogName?: string;
};

/** O&M / robot cleaning — not a PV module; do not treat as generation even though the name contains "module". */
export function isModuleCleaningSystemAssetType(assetType: string): boolean {
  const lower = (assetType || '').trim().toLowerCase();
  return lower.includes('module cleaning');
}

function classifyAssetLabel(raw: string): AssetTypeConfigItem {
  if (ASSET_TYPE_CONFIG[raw]) return ASSET_TYPE_CONFIG[raw];
  const lower = raw.toLowerCase();
  if (lower === 'solar dc layout' || /\bsolar\s+dc\s+layout\b/.test(lower)) {
    return ASSET_TYPE_CONFIG['Solar DC Layout'];
  }
  if (isModuleCleaningSystemAssetType(raw)) {
    return ASSET_TYPE_CONFIG['Module Cleaning System'];
  }
  // Meters, monitors, and energy-measurement equipment are NOT solar modules –
  // check for "meter" / "monitor" / "sensor" BEFORE the broad 'solar' keyword so
  // "Solar Energy Meter" doesn't get mis-categorized as a GENERATION (tiny cell) asset.
  if (lower.includes('meter') || lower.includes('monitor') || lower.includes('sensor')) {
    return ASSET_TYPE_CONFIG['Other'];
  }
  if (lower.includes('ups') || (lower.includes('battery') && !lower.includes('module'))) {
    return ASSET_TYPE_CONFIG['BATTERY CELLS'];
  }
  if (lower.includes('lt panel') || lower.includes('ltp')) {
    return ASSET_TYPE_CONFIG['LT Panel'];
  }
  if (lower.includes('accessory') && (lower.includes('module') || lower.includes('pv'))) {
    return ASSET_TYPE_CONFIG['PV Module Accessories'];
  }
  if (lower.includes('dc cable') || lower === 'dc cable') {
    return ASSET_TYPE_CONFIG['DC Cable'];
  }
  if (lower.includes('ac cable') || lower === 'ac cable') {
    return ASSET_TYPE_CONFIG['AC Cable'];
  }
  if (lower.includes('power transformer') || lower.includes('transformer')) {
    return ASSET_TYPE_CONFIG['Power Transformer'];
  }
  if (lower.includes('spv') || lower.includes('pv array')) {
    return ASSET_TYPE_CONFIG['PV Array Module'];
  }
  if (lower.includes('module') || lower.includes('pv') || lower.includes('solar')) {
    return ASSET_TYPE_CONFIG['Solar Module'];
  }
  if (lower.includes('string') && !lower.includes('inverter')) {
    return ASSET_TYPE_CONFIG['String'];
  }
  if (lower.includes('inverter')) {
    return ASSET_TYPE_CONFIG['Inverter'];
  }
  if (lower.includes('transformer')) return ASSET_TYPE_CONFIG['Transformer'];
  return ASSET_TYPE_CONFIG['Other'];
}

export function getAssetTypeConfig(
  assetType: string,
  availableAssets: AvailableAssetRef[],
  options?: GetAssetTypeConfigOptions
): AssetTypeConfigItem {
  const fromMaster =
    options?.masterAssetId &&
    availableAssets.find(a => a.masterAssetId === options.masterAssetId)?.name;
  const preferred = (options?.catalogName || fromMaster || '').trim();
  const at = (assetType || '').trim();

  // API `assetType` wins for Solar DC Layout: catalog may point at a PV-module project asset
  // (or a mislabeled master) — names like "MODULE CLEANING SYSTEM" still contain "module" and
  // would mis-classify icons/colors if preferred over the equipment row type.
  let primary: string;
  let secondary: string;
  if (isSolarDcLayoutEquipmentType(at)) {
    primary = at || 'Solar DC Layout';
    secondary = preferred && preferred !== primary ? preferred : '';
  } else {
    primary = preferred || at || 'Other';
    secondary = at && primary !== at ? at : '';
  }

  let result = classifyAssetLabel(primary);
  if (result === ASSET_TYPE_CONFIG['Other'] && secondary) {
    result = classifyAssetLabel(secondary);
  }
  return result;
}

export function getAssetCategory(assetType: string): AssetCategory {
  return getAssetTypeConfig(assetType, []).category;
}

export function isGenerationAsset(assetType: string): boolean {
  const cat = getAssetCategory(assetType);
  if (cat === AssetCategory.GENERATION) return true;
  /** DC_SIDE is used for DC Cable (not generation) and future DC bus — only modules count */
  if (cat === AssetCategory.DC_SIDE) {
    return isModuleLikeAssetType(assetType);
  }
  return false;
}

/** True for PV modules / SPV — not DC cable (also DC_SIDE category) */
export function isModuleLikeAssetType(assetType: string): boolean {
  if (isModuleCleaningSystemAssetType(assetType)) return false;
  const lower = assetType.toLowerCase();
  if (lower.includes('cable')) return false;
  if (lower.includes('accessory')) return false;
  return (
    lower.includes('module') ||
    lower.includes('spv') ||
    lower.includes('pv array') ||
    (lower.includes('pv') && !lower.includes('cable')) ||
    (lower.includes('solar') && !lower.includes('cable'))
  );
}

export function isStringAsset(assetType: string): boolean {
  return getAssetCategory(assetType) === AssetCategory.STRING;
}

/** String combiner / junction — DC collection, not a DC String row; must not use string-grid card template */
export function isCombinerBoxAssetType(assetType: string): boolean {
  const lower = assetType.toLowerCase();
  return lower.includes('combiner') && !lower.includes('inverter');
}

/**
 * Inverter equipment for DC layout / virtual string expansion (includes "String Inverter", "STRING INVERTER").
 * Excludes string-only rows (DC String) and string combiner boxes.
 */
export function isInverterEquipmentType(assetType: string, hierarchyLevel?: string): boolean {
  const at = (assetType || '').trim().toLowerCase();
  const hl = (hierarchyLevel || '').trim().toLowerCase();
  if (isCombinerBoxAssetType(assetType)) return false;
  return at.includes('inverter') || hl.includes('inverter');
}

/** Real equipment row that holds `solarDcLayout` (child of inverter or SCB); module checkpoints apply once here. */
export function isSolarDcLayoutEquipmentType(assetType: string, hierarchyLevel?: string): boolean {
  const at = (assetType || '').trim().toLowerCase();
  const hl = (hierarchyLevel || '').trim().toLowerCase();
  return at === 'solar dc layout' || hl === 'solar dc layout';
}

/** Card / chip subtitle: prefer API `assetType` for Solar DC Layout over catalog names. */
export function getEquipmentTypeDisplayLabel(assetType: string, projectAssetName?: string): string {
  if (isSolarDcLayoutEquipmentType(assetType)) {
    return (assetType || 'Solar DC Layout').trim();
  }
  return (projectAssetName || assetType || 'Asset').trim();
}

/**
 * Project-asset strip / catalog tiles: hide String and DC String (module groupers only).
 * Keep names that include combiner or SCB — those are real assets.
 */
export function isStringGroupingProjectAssetName(name: string): boolean {
  const n = (name || '').trim().toLowerCase();
  if (!n) return false;
  if (/\bcombiner\b/.test(n) || /\bscb\b/.test(n)) return false;
  return n === 'string' || n === 'dc string';
}

export function isConversionAsset(assetType: string): boolean {
  return getAssetCategory(assetType) === AssetCategory.CONVERSION;
}
